/**
 * "Kategorisera kunder": kunder som saknar segment eller organisationstyp ur
 * listorna, med AI-förslag. Inget sparas utan att användaren godkänner –
 * per rad eller alla synliga på en gång. Bara fält som saknas ändras; ett
 * giltigt värde som redan finns rörs inte.
 */
import React, { useMemo, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Chip,
  CircularProgress, Link, Pagination, Tooltip,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { SEGMENT_OPTIONS, ORGANISATIONSTYP_OPTIONS } from './kundformular';
import { SegmentValjare, OrganisationstypValjare } from './KategoriVal';
import { foreslaKategorier, OKANT, MAX_PER_ANROP } from './kundKategoriAI';

export const saknarKategori = c =>
  !SEGMENT_OPTIONS.includes(c.segment) || !ORGANISATIONSTYP_OPTIONS.includes(c.organisationstyp);

const PER_SIDA = MAX_PER_ANROP;

export default function KategoriseraKunderDialog({ open, onClose, companies, onSaved }) {
  const lista = useMemo(() => companies.filter(saknarKategori)
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'sv')), [companies]);
  const [sida, setSida] = useState(1);
  const [forslag, setForslag] = useState({}); // id → { segment, organisationstyp, motivering }
  const [val, setVal] = useState({});         // id → { segment, organisationstyp } (användarens val)
  const [hamtar, setHamtar] = useState(false);
  const [sparar, setSparar] = useState(false);
  const [fel, setFel] = useState('');

  const antalSidor = Math.max(1, Math.ceil(lista.length / PER_SIDA));
  const synliga = lista.slice((Math.min(sida, antalSidor) - 1) * PER_SIDA, Math.min(sida, antalSidor) * PER_SIDA);

  // Det som sparas för ett fält: användarens val, annars AI-förslaget (utom okänt).
  // Bara fält som saknas – ett giltigt befintligt värde visas men ändras inte.
  const saknas = (c, f) => !(f === 'segment' ? SEGMENT_OPTIONS : ORGANISATIONSTYP_OPTIONS).includes(c[f]);
  const varde = (c, f) => {
    if (!saknas(c, f)) return '';
    if (val[c.id]?.[f] !== undefined) return val[c.id][f];
    const v = forslag[c.id]?.[f];
    return v && v !== OKANT ? v : '';
  };
  const andringar = c => Object.fromEntries(['segment', 'organisationstyp']
    .map(f => [f, varde(c, f)]).filter(([, v]) => v));

  async function hamtaForslag() {
    const utan = synliga.filter(c => !forslag[c.id]);
    if (!utan.length) return;
    setHamtar(true); setFel('');
    try {
      const svar = await foreslaKategorier(utan);
      setForslag(f => ({ ...f, ...svar }));
    } catch (e) {
      setFel(`AI-förslagen kunde inte hämtas: ${e.message || e}`);
    } finally {
      setHamtar(false);
    }
  }

  async function spara(rader) {
    setSparar(true); setFel('');
    try {
      for (const c of rader) {
        const falt = andringar(c);
        if (!Object.keys(falt).length) continue;
        await updateDoc(doc(db, 'crmCompanies', c.id), { ...falt, updatedAt: new Date() });
        onSaved?.(c.id, falt);
      }
    } catch (e) {
      setFel(`Kunde inte spara: ${e.message || e}`);
    } finally {
      setSparar(false);
    }
  }

  const sattVal = (id, f, v) => setVal(prev => ({ ...prev, [id]: { ...prev[id], [f]: v } }));
  const godkannbara = synliga.filter(c => Object.keys(andringar(c)).length);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1 }}>
          Kategorisera kunder
          <Typography variant="body2" color="text.secondary">
            {lista.length} kunder saknar segment eller organisationstyp. Förslagen sparas först när du godkänner dem.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={hamtar ? <CircularProgress size={14} /> : <AutoFixHighIcon />}
          disabled={hamtar || !synliga.some(c => !forslag[c.id])} onClick={hamtaForslag}>
          AI-förslag för synliga ({synliga.filter(c => !forslag[c.id]).length})
        </Button>
      </DialogTitle>
      <DialogContent dividers>
        {fel && <Alert severity="error" sx={{ mb: 1.5 }}>{fel}</Alert>}
        {!lista.length && <Typography color="text.secondary">Alla kunder har segment och organisationstyp.</Typography>}
        {synliga.map(c => {
          const f = forslag[c.id];
          const falt = andringar(c);
          return (
            <Box key={c.id} data-testid="kategorisera-rad"
              sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 1.2fr) 1fr 1fr auto' },
                gap: 1.5, alignItems: 'center', py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  <Link href={`/crm/companies/${c.id}`} target="_blank" underline="hover" color="inherit">{c.name}</Link>
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div" noWrap>
                  {[c.orgNumber || 'Saknar org.nr', c.city].filter(Boolean).join(' · ')}
                  {c.website && <> · <Link href={/^https?:/.test(c.website) ? c.website : `https://${c.website}`} target="_blank" rel="noopener">{c.website.replace(/^https?:\/\//, '')}</Link></>}
                </Typography>
                {f && (
                  <Tooltip title={f.motivering}>
                    <Typography variant="caption" color="text.disabled" component="div" noWrap>AI: {f.motivering}</Typography>
                  </Tooltip>
                )}
              </Box>
              {['segment', 'organisationstyp'].map(falt2 => {
                const Valjare = falt2 === 'segment' ? SegmentValjare : OrganisationstypValjare;
                if (!saknas(c, falt2)) {
                  return <Typography key={falt2} variant="body2" color="text.secondary">{c[falt2]}</Typography>;
                }
                const fraAI = f && val[c.id]?.[falt2] === undefined;
                return (
                  <Box key={falt2}>
                    <Valjare fullWidth value={varde(c, falt2) || (c[falt2] || '')}
                      onChange={v => sattVal(c.id, falt2, v)} />
                    {fraAI && (
                      <Typography variant="caption" color={f[falt2] === OKANT ? 'warning.main' : 'success.main'}>
                        {f[falt2] === OKANT ? 'AI: okänt – välj själv' : 'AI-förslag'}
                      </Typography>
                    )}
                  </Box>
                );
              })}
              <Button size="small" variant="contained" disabled={sparar || !Object.keys(falt).length}
                onClick={() => spara([c])}>Godkänn</Button>
            </Box>
          );
        })}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Pagination count={antalSidor} page={Math.min(sida, antalSidor)} onChange={(_, p) => setSida(p)} size="small" />
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {godkannbara.length > 0 && <Chip size="small" label={`${godkannbara.length} synliga klara att godkänna`} />}
          <Button onClick={onClose}>Stäng</Button>
          <Button variant="contained" disabled={sparar || !godkannbara.length} onClick={() => spara(godkannbara)}>
            Godkänn alla synliga ({godkannbara.length})
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
